import { BrowserLocalCapture, type LocalCapture } from '../audio/local-capture.js';
import localCaptureUrl from '../audio/local-capture-worklet.ts?worker&url';
import type { ApiClient, ChatSource } from '../api/client.js';
import type { DesktopBridge } from '../session/controller.js';

export interface LocalCoordinatorHooks {
  transcript(role:'user'|'assistant',text:string):void;
  sources?(sources:ChatSource[]):void;
  state(state:string):void;
  error(message:string):void;
}
export interface LocalConversationOptions {mode?:'ptt'|'vad';wakeEnabled?:boolean;micEnabled?:boolean}
const RATE=16000;
export class LocalConversationCoordinator {
  private session=0;
  private turn=0;
  private conversationId:string|undefined;
  private capture:LocalCapture|null=null;
  private controller:AbortController|null=null;
  private frames:number[]=[];
  private preroll:number[]=[];
  private silence=0;
  private voiced=0;
  private muted=false;
  private busy=false;
  private active=false;
  private pressed=false;
  private pressWanted=false;
  private mode:'ptt'|'vad'='ptt';
  private armed=false;
  constructor(private readonly bridge:NonNullable<DesktopBridge['local']>,private readonly api:Pick<ApiClient,'chat'>,private readonly hooks:LocalCoordinatorHooks,private readonly captureFactory:()=>LocalCapture=()=>new BrowserLocalCapture(localCaptureUrl)){}
  async start(options:LocalConversationOptions={}):Promise<void>{
    if(this.active||this.capture) throw new Error('La sesión local ya está activa.');
    const session=++this.session;
    const status=await this.bridge.status();
    if(session!==this.session)return;
    if(!status.whisper.installed||!status.whisper.available||!status.tts.available)throw new Error('Whisper y voz local deben estar disponibles.');
    this.mode=options.mode??'ptt';this.armed=options.wakeEnabled===true;this.muted=false;this.busy=false;this.conversationId=undefined;this.resetSegment();
    if(options.micEnabled!==false){
      const capture=this.captureFactory();this.capture=capture;
      capture.frames(frame=>{if(session===this.session)this.onFrame(frame);});
      try{await capture.start();}catch(error){if(session===this.session){this.capture=null;this.active=false;}await capture.stop();throw error;}
      if(session!==this.session){await capture.stop();return;}
    }else this.armed=false;
    this.active=true;this.resumeCapture();this.hooks.state(this.armed?'waiting':'listening');
  }
  private resetSegment():void{this.frames=[];this.preroll=[];this.silence=0;this.voiced=0;}
  private resumeCapture():void{if(this.active&&!this.muted&&!this.busy)this.capture?.setEnabled(this.mode==='vad'||this.armed);}
  private onFrame(frame:Float32Array):void{
    if(!this.active||this.muted||this.busy||(!this.armed&&this.mode==='ptt'&&!this.pressed))return;
    let sum=0;for(const value of frame){if(!Number.isFinite(value))return;sum+=value*value;}
    const speech=frame.length>0&&Math.sqrt(sum/frame.length)>0.018;
    if(!this.frames.length&&!speech&&!this.pressed){this.preroll.push(...frame);if(this.preroll.length>3200)this.preroll.splice(0,this.preroll.length-3200);return;}
    if(!this.frames.length&&this.preroll.length){this.frames.push(...this.preroll);this.preroll=[];}
    this.frames.push(...frame);
    if(speech){this.voiced+=frame.length;this.silence=0;}else this.silence+=frame.length;
    if(this.frames.length>=15*RATE){this.frames.length=15*RATE;this.pressed=false;this.pressWanted=false;void this.commit();}
    else if((this.mode==='vad'||this.armed)&&this.silence>=.65*RATE)void this.commit();
  }
  private async commit():Promise<void>{
    if(this.busy||!this.active||this.muted)return;
    const samples=this.frames;const voice=this.voiced;this.resetSegment();
    if(voice<.3*RATE||samples.length<.3*RATE)return;
    const turn=++this.turn;this.busy=true;this.capture?.setEnabled(false);this.hooks.state('thinking');
    try{
      const result=await this.bridge.transcribe({samples,sampleRate:16000});
      if(!this.valid(turn))return;
      let text=result.text.trim().slice(0,2000);
      if(this.armed){const wake=isWakePrefix(text);if(!wake.activated)return;this.armed=false;text=wake.remainder;}
      if(text)await this.requestText(text,turn);
    }catch(error){if(this.valid(turn))this.hooks.error((error as Error).message);}
    finally{this.finishTurn(turn);}
  }
  private valid(turn:number):boolean{return this.active&&!this.muted&&turn===this.turn;}
  private finishTurn(turn:number):void{if(turn!==this.turn)return;this.controller=null;this.busy=false;if(this.active){this.resumeCapture();this.hooks.state(this.muted?'muted':this.armed?'waiting':'listening');}}
  private async requestText(text:string,turn:number):Promise<void>{
    if(!this.valid(turn))return;
    this.hooks.transcript('user',text);this.hooks.state('thinking');
    const controller=new AbortController();this.controller=controller;
    const requestId=crypto.randomUUID();
    const response=await this.api.chat({text,requestId,...(this.conversationId?{conversationId:this.conversationId}:{})},controller.signal);
    if(!this.valid(turn))return;
    if(response.requestId!==requestId||typeof response.conversationId!=='string'||typeof response.text!=='string')throw new Error('Respuesta de conversación no válida.');
    this.conversationId=response.conversationId;
    this.hooks.transcript('assistant',response.text);
    if (Array.isArray(response.sources)) this.hooks.sources?.(response.sources);
    this.hooks.state('speaking');
    await this.bridge.speak({text:response.text});
  }
  async sendText(text:string):Promise<void>{
    if(!this.active)return;
    const trimmed=text.trim();if(!trimmed||trimmed.length>2000)throw new Error('El mensaje debe tener entre 1 y 2.000 caracteres.');
    if(this.armed)throw new Error('Desactiva la espera de Oye ASIS antes de enviar texto.');
    await this.interrupt();if(!this.active||this.muted)return;
    const turn=++this.turn;this.busy=true;this.capture?.setEnabled(false);
    try{await this.requestText(trimmed,turn);}catch(error){if(this.valid(turn))this.hooks.error((error as Error).message);}finally{this.finishTurn(turn);}
  }
  async pttDown():Promise<void>{
    if(!this.active||this.mode!=='ptt'||this.armed||this.muted||this.pressWanted)return;
    this.pressWanted=true;await this.interrupt(true);
    if(!this.active||!this.pressWanted||this.muted)return;
    this.resetSegment();this.pressed=true;this.capture?.setEnabled(true);this.hooks.state('listening');
  }
  async pttUp():Promise<void>{
    this.pressWanted=false;if(!this.pressed)return;
    const turn=this.turn;
    try{await this.capture?.flush();if(turn!==this.turn||!this.active)return;this.pressed=false;await this.commit();}
    catch(error){if(turn===this.turn){this.pressed=false;this.resetSegment();this.hooks.error((error as Error).message);}}
  }
  pttCancel():void{this.pressWanted=false;this.pressed=false;this.resetSegment();this.capture?.setEnabled(false);}
  setMuted(value:boolean):void{this.muted=value;if(value)void this.interrupt();else{this.resumeCapture();this.hooks.state(this.armed?'waiting':'listening');}}
  async interrupt(keepPress=false):Promise<void>{
    const turn=++this.turn;this.controller?.abort();this.controller=null;this.busy=false;
    this.pressed=false;if(!keepPress)this.pressWanted=false;this.resetSegment();this.capture?.setEnabled(false);
    await this.bridge.stop();
    if(turn===this.turn&&this.active){this.resumeCapture();this.hooks.state(this.muted?'muted':this.armed?'waiting':'listening');}
  }
  async stop():Promise<void>{
    this.active=false;++this.session;++this.turn;this.controller?.abort();this.controller=null;this.busy=false;this.pressed=false;this.pressWanted=false;this.armed=false;this.resetSegment();this.conversationId=undefined;
    const capture=this.capture;this.capture=null;
    await Promise.all([this.bridge.stop(),capture?.stop()]);this.hooks.state('disconnected');
  }
}
export function isWakePrefix(text:string):{activated:boolean;remainder:string}{
 const normalized=text.normalize('NFD').replace(/[\u0300-\u036f]/gu,'').toLocaleLowerCase('es').replace(/[^\p{L}\p{N}]+/gu,' ').trim();
 const match=/^(?:oye asis|hey asis)(?:\s+|$)/u.exec(normalized);
 return match?{activated:true,remainder:normalized.slice(match[0].length).trim()}:{activated:false,remainder:''};
}
